import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { fetchPatients, fetchArchivedSessions } from '../services/api';
import './BehandlungsverfolgungPage.css';

const TREATMENT_COLORS = [
  '#6366f1', '#0ea5e9', '#10b981', '#f59e0b',
  '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16',
];

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateShort(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
}

function monthsBetween(a, b) {
  const da = new Date(a), db = new Date(b);
  return (db.getFullYear() - da.getFullYear()) * 12 + (db.getMonth() - da.getMonth());
}

function groupByTreatment(sessions) {
  const map = {};
  sessions.forEach(s => {
    const key = s.diagnoses?.[0]?.name || 'Sonstiges';
    if (!map[key]) map[key] = [];
    map[key].push(s);
  });
  Object.values(map).forEach(arr =>
    arr.sort((a, b) => new Date(a.session_date) - new Date(b.session_date))
  );
  return map;
}

function isOngoing(name) {
  const l = name.toLowerCase();
  return !l.includes('abgeheilt') && !l.includes('remittiert') && !l.includes('abgeschlossen');
}

// Extrahiert kurze Zusammenfassung für die Kachel (max. 1 Satz je Bereich)
function getTileContent(sessions) {
  const sorted = [...sessions].sort((a, b) => new Date(a.session_date) - new Date(b.session_date));
  const first = sorted[0];
  const last = sorted[sorted.length - 1];

  const findSection = (s, keywords) =>
    (s.summary || []).find(sec => keywords.some(k => (sec.title || '').toLowerCase().includes(k)));

  const anamSec = findSection(first, ['anamnese', 'hauptbeschwerde', 'beschwerde', 'symptom', 'verlauf']);
  const anamnese = anamSec?.items?.[0] || '';

  const befundSec = findSection(first, ['befund', 'diagnostik', 'labor', 'untersuchung']);
  const befund = befundSec?.items?.[0] || '';

  const therSec = findSection(last, ['therapie', 'medikation', 'behandlung', 'procedere', 'empfehlung']);
  const behandlung = therSec?.items?.[0] || '';

  return { anamnese, befund, behandlung };
}

// Gruppiert Session-Summary in 3 Kacheln für Detailansicht
function getDetailSections(session) {
  const summary = session.summary || [];
  // Anamnese: Patientenstatus, Beschwerden, Verlauf in Folgesessions
  const anamKeys = ['anamnese', 'beschwerde', 'symptom', 'vorgeschichte', 'verlauf'];
  // Befund: klinische Befunde, Labor, Diagnostik-Ergebnisse
  const befundKeys = ['befund', 'labor', 'untersuchung', 'diagnostik'];
  // Therapie: Behandlung, Medikation, Therapiepläne, Procedere
  const therKeys = ['therapie', 'behandlung', 'medikation', 'medikament', 'empfehlung', 'plan', 'maßnahme', 'procedere'];

  const anamneseSecs = summary.filter(s => anamKeys.some(k => (s.title || '').toLowerCase().includes(k)));
  const befundSecs = summary.filter(s => befundKeys.some(k => (s.title || '').toLowerCase().includes(k)));
  const therapieSecs = summary.filter(s => therKeys.some(k => (s.title || '').toLowerCase().includes(k)));
  const coveredTitles = new Set([...anamneseSecs, ...befundSecs, ...therapieSecs].map(s => s.title));
  const rest = summary.filter(s => !coveredTitles.has(s.title));
  return {
    anamnese: anamneseSecs,
    befund: befundSecs,
    diagnose: session.diagnoses || [],
    therapie: [...therapieSecs, ...rest],
  };
}

// ── Tracking-Datengenerator ───────────────────────────────────────────────────

function seededRnd(seed) {
  let s = (Math.abs(seed) & 0x7fffffff) || 1;
  return () => { s = (s * 1664525 + 1013904223) & 0x7fffffff; return s / 0x7fffffff; };
}

function getMetricDefs(diagnosisName) {
  const d = diagnosisName.toLowerCase();
  if (d.includes('khk') || d.includes('koronar') || d.includes('ptca') || d.includes('herzinsuff')) return [
    { id: 'brustschmerz', label: 'Brustschmerzen', unit: 'Score 0–10', min: 0, max: 10, start: 7.2, end: 2.3, noise: 0.7, color: '#ef4444', lowerBetter: true },
    { id: 'belastbarkeit', label: 'Belastbarkeit', unit: 'Score 0–10', min: 0, max: 10, start: 2.8, end: 6.8, noise: 0.6, color: '#10b981', lowerBetter: false },
    { id: 'medikation', label: 'Medikamenteneinnahme', unit: '%', min: 50, max: 100, start: 71, end: 92, noise: 4, color: '#6366f1', lowerBetter: false },
  ];
  if (d.includes('diabetes') || d.includes('blutzucker')) return [
    { id: 'blutzucker', label: 'Nüchternblutzucker', unit: 'mg/dL', min: 80, max: 220, start: 167, end: 113, noise: 11, color: '#f59e0b', lowerBetter: true },
    { id: 'wohlbefinden', label: 'Wohlbefinden', unit: 'Score 0–10', min: 0, max: 10, start: 3.5, end: 6.8, noise: 0.7, color: '#0ea5e9', lowerBetter: false },
    { id: 'medikation', label: 'Medikamenteneinnahme', unit: '%', min: 50, max: 100, start: 67, end: 88, noise: 6, color: '#6366f1', lowerBetter: false },
  ];
  if (d.includes('hypertonie') || d.includes('blutdruck')) return [
    { id: 'rr_syst', label: 'Blutdruck systolisch', unit: 'mmHg', min: 110, max: 200, start: 168, end: 133, noise: 7, color: '#ef4444', lowerBetter: true },
    { id: 'medikation', label: 'Medikamenteneinnahme', unit: '%', min: 50, max: 100, start: 79, end: 94, noise: 4, color: '#6366f1', lowerBetter: false },
  ];
  if (d.includes('burnout') || d.includes('depress')) return [
    { id: 'stimmung', label: 'Stimmung', unit: 'Score 0–10', min: 0, max: 10, start: 1.8, end: 7.5, noise: 0.9, color: '#f59e0b', lowerBetter: false },
    { id: 'energie', label: 'Energie', unit: 'Score 0–10', min: 0, max: 10, start: 1.5, end: 6.3, noise: 0.8, color: '#10b981', lowerBetter: false },
    { id: 'schlaf', label: 'Schlafqualität', unit: 'Score 0–10', min: 0, max: 10, start: 2.5, end: 7.2, noise: 1.0, color: '#0ea5e9', lowerBetter: false },
    { id: 'medikation', label: 'Medikamenteneinnahme', unit: '%', min: 30, max: 100, start: 60, end: 84, noise: 7, color: '#6366f1', lowerBetter: false },
  ];
  if (d.includes('lumbalgie') || d.includes('rücken')) return [
    { id: 'schmerz', label: 'Schmerzintensität', unit: 'Score 0–10', min: 0, max: 10, start: 7.8, end: 3.8, noise: 1.1, color: '#ef4444', lowerBetter: true },
    { id: 'mobilitaet', label: 'Mobilität', unit: 'Score 0–10', min: 0, max: 10, start: 2.5, end: 6.5, noise: 0.7, color: '#10b981', lowerBetter: false },
    { id: 'physio', label: 'Physio-Adherenz', unit: '%', min: 0, max: 100, start: 52, end: 80, noise: 10, color: '#8b5cf6', lowerBetter: false },
  ];
  if (d.includes('migräne') || d.includes('migrane') || d.includes('kopfschmerz')) return [
    { id: 'anfaelle', label: 'Anfallstage / Monat', unit: 'Tage', min: 0, max: 20, start: 8.5, end: 2.8, noise: 1.4, color: '#ef4444', lowerBetter: true },
    { id: 'schmerzst', label: 'Schmerzstärke Anfälle', unit: 'Score 0–10', min: 0, max: 10, start: 8.5, end: 5.2, noise: 0.8, color: '#f97316', lowerBetter: true },
  ];
  if (d.includes('rhinitis') || d.includes('allergi') || d.includes('scit')) return [
    { id: 'symptome', label: 'Symptomstärke', unit: 'Score 0–10', min: 0, max: 10, start: 7.2, end: 3.5, noise: 1.0, color: '#f59e0b', lowerBetter: true },
    { id: 'scit', label: 'SCIT-Adherenz', unit: '%', min: 60, max: 100, start: 95, end: 97, noise: 2, color: '#6366f1', lowerBetter: false },
    { id: 'lebensqual', label: 'Lebensqualität', unit: 'Score 0–10', min: 0, max: 10, start: 3.5, end: 7.2, noise: 0.7, color: '#10b981', lowerBetter: false },
  ];
  if (d.includes('bandläsion') || d.includes('osg') || d.includes('knöchel') || d.includes('sprunggelenk')) return [
    { id: 'schmerz', label: 'Schmerzintensität', unit: 'Score 0–10', min: 0, max: 10, start: 8.5, end: 0.5, noise: 0.6, color: '#ef4444', lowerBetter: true },
    { id: 'mobilitaet', label: 'Mobilität / Belastung', unit: 'Score 0–10', min: 0, max: 10, start: 1.8, end: 9.2, noise: 0.5, color: '#10b981', lowerBetter: false },
    { id: 'physio', label: 'Physio-Adherenz', unit: '%', min: 60, max: 100, start: 92, end: 96, noise: 4, color: '#8b5cf6', lowerBetter: false },
  ];
  if (d.includes('dermatitis') || d.includes('ekzem') || d.includes('atop')) return [
    { id: 'juckreiz', label: 'Juckreizintensität', unit: 'Score 0–10', min: 0, max: 10, start: 7.8, end: 3.2, noise: 1.4, color: '#ef4444', lowerBetter: true },
    { id: 'schlaf', label: 'Schlafstörung', unit: 'Score 0–10', min: 0, max: 10, start: 6.8, end: 2.5, noise: 1.2, color: '#f59e0b', lowerBetter: true },
    { id: 'lokalther', label: 'Lokaltherapie-Adherenz', unit: '%', min: 0, max: 100, start: 56, end: 79, noise: 8, color: '#6366f1', lowerBetter: false },
  ];
  return [
    { id: 'allgemein', label: 'Allgemeinzustand', unit: 'Score 0–10', min: 0, max: 10, start: 4, end: 7, noise: 0.8, color: '#6366f1', lowerBetter: false },
    { id: 'medikation', label: 'Medikamenteneinnahme', unit: '%', min: 0, max: 100, start: 70, end: 85, noise: 5, color: '#10b981', lowerBetter: false },
  ];
}

function generateTrackingMetrics(patientLabel, treatmentName, sessions) {
  if (!sessions || sessions.length === 0) return [];
  const sorted = [...sessions].sort((a, b) => new Date(a.session_date) - new Date(b.session_date));
  const startDate = new Date(sorted[0].session_date);
  const ongoing = isOngoing(treatmentName);
  let endDate = ongoing ? new Date() : new Date(sorted[sorted.length - 1].session_date);
  if (!ongoing) endDate = new Date(endDate.getTime() + 16 * 86400000);

  const defs = getMetricDefs(treatmentName);
  const seedNum = [...patientLabel, ...treatmentName].reduce((a, c) => a + c.charCodeAt(0), 0);
  const rnd = seededRnd(seedNum);
  const sessionDates = sorted.map(s => s.session_date.slice(0, 10));
  const totalMs = endDate - startDate || 1;

  return defs.map(def => {
    const data = [];
    const cur = new Date(startDate);
    while (cur <= endDate) {
      const progress = (cur - startDate) / totalMs;
      const ease = progress < 0.5 ? 2 * progress * progress : 1 - (-2 * progress + 2) ** 2 / 2;
      let boost = 0;
      sorted.forEach(s => {
        const days = (cur - new Date(s.session_date)) / 86400000;
        if (days >= 0 && days <= 14) boost += 0.45 * Math.exp(-days / 7);
      });
      const base = def.start + (def.end - def.start) * ease;
      const boostEffect = (def.lowerBetter ? -1 : 1) * boost * Math.abs(def.end - def.start) * 0.12;
      let val = base + boostEffect + (rnd() - 0.5) * 2 * def.noise;
      val = Math.max(def.min, Math.min(def.max, val));
      const isInt = def.unit === '%' || def.unit.includes('mg/dL') || def.unit.includes('mmHg') || def.unit.includes('Tage');
      data.push({ date: cur.toISOString().slice(0, 10), value: isInt ? Math.round(val) : Math.round(val * 10) / 10 });
      cur.setDate(cur.getDate() + 3 + Math.floor(rnd() * 3));
    }
    return { ...def, data, sessionDates };
  });
}

function aggregateByRes(data, resolution) {
  if (resolution === 'daily' || data.length === 0) return data;
  const buckets = {};
  data.forEach(({ date, value }) => {
    const d = new Date(date);
    const key = resolution === 'weekly'
      ? `${d.getFullYear()}-W${String(Math.ceil(((d - new Date(d.getFullYear(), 0, 1)) / 86400000 + new Date(d.getFullYear(), 0, 1).getDay() + 1) / 7)).padStart(2, '0')}`
      : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (!buckets[key]) buckets[key] = { firstDate: date, sum: 0, count: 0 };
    buckets[key].sum += value;
    buckets[key].count++;
  });
  return Object.values(buckets).map(b => ({ date: b.firstDate, value: Math.round((b.sum / b.count) * 10) / 10 }));
}

// ── Patientenkommentar-Generator ─────────────────────────────────────────────

function getCommentTemplates(diagnosisName) {
  const d = diagnosisName.toLowerCase();
  if (d.includes('khk') || d.includes('koronar') || d.includes('ptca')) return [
    { text: 'Atemnot beim Treppensteigen – ist das die Ticagrelor-Nebenwirkung, die Sie erklärt hatten?', type: 'sideeffect' },
    { text: 'Muskelziehen Oberschenkel links seit ca. 1 Woche, besonders abends. Kann das vom Atorvastatin kommen?', type: 'sideeffect' },
    { text: 'Heute Abenddosis Bisoprolol vergessen – war das problematisch? Soll ich sie noch nachts nehmen?', type: 'adherence' },
    { text: 'Blutdruck heute Morgen: 152/90 mmHg. Ist das noch in Ordnung oder soll ich melden?', type: 'concern' },
    { text: 'Spaziergang heute 30 Minuten – kein Angina-Schmerz mehr! Fühle mich deutlich besser.', type: 'positive' },
    { text: 'Kurzes Herzstolpern beim Treppensteigen heute. Soll ich das im Tagebuch festhalten?', type: 'concern' },
    { text: 'ASS und Ticagrelor nehme ich regelmäßig, aber Ramipril vergesse ich manchmal abends.', type: 'adherence' },
    { text: '45 Minuten Nordic Walking ohne Beschwerden – kein Vergleich zu vor 6 Monaten!', type: 'positive' },
    { text: 'Leichter Schwindel beim Aufstehen heute Morgen – ist das Bisoprolol-bedingt?', type: 'concern' },
  ];
  if (d.includes('diabetes') || d.includes('blutzucker')) return [
    { text: 'Nüchternblutzucker heute Morgen 178 mg/dl trotz Metformin. Ist das am Anfang normal?', type: 'concern' },
    { text: 'Bauchschmerzen und leichte Übelkeit nach Metformin-Einnahme. Wie lange dauert das an?', type: 'sideeffect' },
    { text: 'Bei Geburtstag Kuchen gegessen – Blutzucker danach 214 mg/dl. War wohl ein Fehler.', type: 'setback' },
    { text: 'Diese Woche 3× spazieren gewesen. Bin gespannt auf das nächste HbA1c!', type: 'positive' },
    { text: 'Empagliflozin jetzt 2 Wochen – öfter zur Toilette. Das ist wohl normal?', type: 'sideeffect' },
    { text: 'Diabetesschulung abgeschlossen – jetzt verstehe ich endlich den Zusammenhang zwischen Kohlenhydraten und Blutzucker.', type: 'positive' },
    { text: 'Nüchternblutzucker heute: 118 mg/dl – das beste Ergebnis bisher!', type: 'positive' },
  ];
  if (d.includes('hypertonie') || d.includes('blutdruck')) return [
    { text: 'Heute Blutdruck 162/98 – sehr hoch. Soll ich die Dosis anpassen oder warten?', type: 'concern' },
    { text: 'Seit Beginn des Amlodipins: beide Knöchel etwas geschwollen, abends stärker. Bedenklich?', type: 'sideeffect' },
    { text: 'Blutdrucktagebuch diese Woche: Ø 138/84 mmHg. Bessert sich!', type: 'positive' },
    { text: 'Kochsalz stark reduziert – alle Gerichte schmecken jetzt so fade. Wann gewöhnt man sich daran?', type: 'adherence' },
    { text: 'Ramipril heute Morgen vergessen. Kann ich sie noch nachmittags nehmen?', type: 'adherence' },
    { text: 'Nach Urlaub Blutdruck wieder gestiegen: 156/94. Wahrscheinlich zu viel Restaurantessen.', type: 'setback' },
    { text: 'Zielwert heute: 127/78 mmHg! Bin sehr zufrieden.', type: 'positive' },
  ];
  if (d.includes('burnout') || d.includes('depress')) return [
    { text: 'Sertralin jetzt 3 Wochen – noch kaum Wirkung. Wie lange dauert es noch?', type: 'concern' },
    { text: 'Sehr unruhig und angespannt diese Woche, besonders morgens. Ist das die Eingewöhnung?', type: 'sideeffect' },
    { text: 'Erste Nacht mit 6 Stunden Schlaf am Stück – das erste Mal seit Monaten!', type: 'positive' },
    { text: 'Psychotherapie letzte Woche sehr intensiv – emotionaler als erwartet, aber gut.', type: 'concern' },
    { text: 'Erster Arbeitstag in der Wiedereingliederung – besser als befürchtet.', type: 'positive' },
    { text: 'Manchmal vergesse ich Sertralin abends, weil ich so früh einschlafe.', type: 'adherence' },
    { text: 'PHQ-9 selbst ausgefüllt: 5 Punkte – letztes Mal waren es 14! Das motiviert mich sehr.', type: 'positive' },
  ];
  if (d.includes('lumbalgie') || d.includes('rücken')) return [
    { text: 'Physiotherapie heute sehr schmerzhaft – ist das normal? Habe Angst, etwas zu verschlimmern.', type: 'concern' },
    { text: 'Erste fast schmerzfreie Nacht seit Wochen!', type: 'positive' },
    { text: 'Höhenverstellbarer Schreibtisch angekommen – nachmittags deutlich weniger Verspannung.', type: 'positive' },
    { text: '4 Stunden Autofahrt letzten Samstag – danach Schmerzen wieder NRS 7/10. Rückschlag.', type: 'setback' },
    { text: 'Ibuprofen hilft gut, aber ich bekomme Magenbeschwerden davon.', type: 'sideeffect' },
    { text: 'Heimübungen leider diese Woche kaum gemacht – Projektdeadline wieder mal.', type: 'adherence' },
    { text: 'Duloxetin jetzt 2 Wochen – leichter Schwindel morgens, sonst gut verträglich.', type: 'sideeffect' },
    { text: 'Nordic Walking 3× diese Woche – Schmerzen beim Gehen merklich besser.', type: 'positive' },
  ];
  if (d.includes('migräne') || d.includes('migrane') || d.includes('kopfschmerz')) return [
    { text: 'Migränetagebuch: Diese Woche keine einzige Attacke! Absoluter Rekord.', type: 'positive' },
    { text: 'Rotwein am Samstag – Migräne in der Nacht, NRS 9/10. Lektion gelernt.', type: 'setback' },
    { text: 'Sumatriptan hat die Attacke nach ca. 40 Minuten gestoppt – danke für den Tipp!', type: 'positive' },
    { text: 'Metoprolol seit 4 Wochen – nachmittags manchmal etwas schläfrig. Bessert sich das?', type: 'sideeffect' },
    { text: 'Nur noch 1 Attacke diesen Monat! MIDAS-Score laut App auf 6 gesunken.', type: 'positive' },
    { text: 'Schlafmangel am Wochenende – direkt Migräne am Montag. Zusammenhang ist eindeutig.', type: 'concern' },
  ];
  if (d.includes('rhinitis') || d.includes('allergi') || d.includes('scit')) return [
    { text: 'Nach letzter SCIT-Injektion: Juckreiz am Arm für ca. 2 Stunden. Ist das normal?', type: 'sideeffect' },
    { text: 'Diese Pollensaison deutlich besser – erst 2 wirklich schlechte Tage bisher!', type: 'positive' },
    { text: 'Mometason-Spray 3 Tage vergessen – Niesattacken kamen sofort zurück.', type: 'adherence' },
    { text: 'Reise im August: Wie organisiere ich die SCIT-Injektion? Beim Allergologen vor Ort?', type: 'concern' },
    { text: 'Augen kaum noch gereizt dieses Jahr – SCIT scheint wirklich zu helfen!', type: 'positive' },
    { text: 'Pollenflugkalender-App ist sehr hilfreich für die Tagesplanung.', type: 'positive' },
  ];
  if (d.includes('bandläsion') || d.includes('osg') || d.includes('knöchel') || d.includes('sprunggelenk')) return [
    { text: 'Physiotherapie heute: Konnte schon auf einem Bein balancieren – Fortschritt!', type: 'positive' },
    { text: 'Beim schnellen Gehen knackt der Knöchel noch. Ist das bedenklich?', type: 'concern' },
    { text: 'Erstes Mal Fahrrad fahren nach 5 Wochen – hat gut geklappt!', type: 'positive' },
    { text: 'Gelenk bei Regen empfindlicher – ist das Einbildung oder ein Zeichen?', type: 'concern' },
    { text: 'Aircast-Schiene beim Spazieren hilft sehr – ohne sie bin ich noch unsicher.', type: 'positive' },
    { text: 'Kurz beim Fußball mitgemacht – kein Schmerz! Glaube ich bin fast wieder fit.', type: 'positive' },
  ];
  if (d.includes('dermatitis') || d.includes('ekzem') || d.includes('atop')) return [
    { text: 'Kratzen nachts leider wieder stärker – neues Waschmittel ausprobiert, scheint besser.', type: 'concern' },
    { text: 'Tacrolimus anfangs Brennen für ca. 20 Minuten – aber jetzt gewöhnt und nicht mehr.', type: 'sideeffect' },
    { text: 'Basistherapie jetzt konsequent 2× täglich – Juckreiz deutlich weniger geworden!', type: 'positive' },
    { text: 'Hitzewelle letztes Wochenende: Schub an Kniekehlen und Ellenbeugen.', type: 'setback' },
    { text: 'Dexpanthenol-Lotion nach dem Duschen: Haut fühlt sich so viel besser an!', type: 'positive' },
    { text: 'SCORAD-Selbsteinschätzung: gefühlt unter 10 – das wäre super!', type: 'positive' },
  ];
  return [
    { text: 'Medikamente regelmäßig eingenommen – manchmal vergesse ich abends.', type: 'adherence' },
    { text: 'Symptome bessern sich langsam. Ich bleibe geduldig.', type: 'positive' },
    { text: 'Nebenwirkungen halten sich in Grenzen bisher.', type: 'positive' },
    { text: 'Rückschlag diese Woche – Beschwerden waren kurz stärker.', type: 'setback' },
  ];
}

function shuffleSeeded(arr, rnd) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function generatePatientComments(patientLabel, treatmentName, sessions) {
  if (!sessions || sessions.length === 0) return [];
  const sorted = [...sessions].sort((a, b) => new Date(a.session_date) - new Date(b.session_date));
  const startDate = new Date(sorted[0].session_date);
  const ongoing = isOngoing(treatmentName);
  const endDate = ongoing ? new Date() : new Date(sorted[sorted.length - 1].session_date);
  const totalMs = endDate - startDate || 1;

  const templates = getCommentTemplates(treatmentName);
  const seedNum = [...patientLabel, ...treatmentName, 'comments'].reduce((a, c) => a + c.charCodeAt(0), 0);
  const rnd = seededRnd(seedNum);
  const numComments = Math.min(templates.length, 5 + Math.floor(rnd() * 3));
  const shuffled = shuffleSeeded(templates, rnd).slice(0, numComments);

  return shuffled.map((t, i) => {
    const progress = (i + 0.6 + rnd() * 0.6) / (numComments + 1);
    const date = new Date(startDate.getTime() + Math.min(progress, 0.96) * totalMs);
    return { date: date.toISOString().slice(0, 10), text: t.text, type: t.type };
  }).sort((a, b) => new Date(a.date) - new Date(b.date));
}

const COMMENT_TYPE_META = {
  positive:   { label: 'Fortschritt', color: '#10b981', bg: '#f0fdf4', border: '#bbf7d0' },
  concern:    { label: 'Rückfrage',   color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  sideeffect: { label: 'Nebenwirkung',color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
  adherence:  { label: 'Einnahme',    color: '#6366f1', bg: '#eef2ff', border: '#c7d2fe' },
  setback:    { label: 'Rückschlag',  color: '#8b5cf6', bg: '#faf5ff', border: '#e9d5ff' },
};

// ── TrackingChart (SVG) ───────────────────────────────────────────────────────

function TrackingChart({ metric, resolution, selectedSessionDateStr }) {
  const [tooltip, setTooltip] = useState(null);
  const data = useMemo(() => aggregateByRes(metric.data, resolution), [metric.data, resolution]);
  if (!data || data.length < 2) return null;

  const VW = 700, VH = 176;
  const PL = 40, PR = 14, PT = 10, PB = 28;
  const cW = VW - PL - PR, cH = VH - PT - PB;

  const ts = data.map(d => new Date(d.date).getTime());
  const minT = Math.min(...ts), maxT = Math.max(...ts);
  const span = maxT - minT || 1;
  const minV = metric.min, maxV = metric.max;
  const vSpan = maxV - minV || 1;

  const xFor = t => PL + (t - minT) / span * cW;
  const yFor = v => PT + cH - (v - minV) / vSpan * cH;

  const pts = data.map(d => `${xFor(new Date(d.date).getTime()).toFixed(1)},${yFor(d.value).toFixed(1)}`).join(' ');
  const areaPath = [
    `${PL},${PT + cH}`,
    ...data.map(d => `${xFor(new Date(d.date).getTime()).toFixed(1)},${yFor(d.value).toFixed(1)}`),
    `${xFor(new Date(data[data.length - 1].date).getTime()).toFixed(1)},${PT + cH}`,
  ].join(' ');

  // X-Ticks
  const xTicks = [];
  {
    const start = new Date(minT), end = new Date(maxT);
    const totalMonths = (end.getFullYear() - start.getFullYear()) * 12 + end.getMonth() - start.getMonth();
    const step = totalMonths <= 3 ? 1 : totalMonths <= 12 ? 2 : totalMonths <= 24 ? 3 : 6;
    const c = new Date(start.getFullYear(), start.getMonth(), 1);
    c.setMonth(Math.ceil(c.getMonth() / step) * step);
    while (c <= end) {
      const x = xFor(c.getTime());
      if (x >= PL && x <= VW - PR)
        xTicks.push({ x, label: c.toLocaleDateString('de-DE', { month: 'short', year: '2-digit' }) });
      c.setMonth(c.getMonth() + step);
    }
  }

  const yLabels = [minV, Math.round((minV + maxV) / 2 * 10) / 10, maxV];

  const markers = metric.sessionDates.map(sd => {
    const t = new Date(sd).getTime();
    if (t < minT - 86400000 || t > maxT + 86400000) return null;
    return { x: xFor(t), isSelected: sd === selectedSessionDateStr };
  }).filter(Boolean);

  // Tooltip rendering (SVG overlay)
  const renderTooltip = () => {
    if (!tooltip) return null;
    const TW = 110, TH = 34, pad = 6;
    const tx = Math.min(tooltip.x + 6, VW - PR - TW - 2);
    const ty = Math.max(PT, tooltip.y - TH - 4);
    const dateStr = new Date(tooltip.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    return (
      <g style={{ pointerEvents: 'none' }}>
        <rect x={tx} y={ty} width={TW} height={TH} rx={4} fill="#1f2937" opacity="0.88" />
        <text x={tx + pad} y={ty + 13} fontSize="8.5" fill="#e5e7eb">{dateStr}</text>
        <text x={tx + pad} y={ty + 26} fontSize="9.5" fontWeight="600" fill="#fff">{tooltip.value} {metric.unit}</text>
      </g>
    );
  };

  return (
    <div className="tracking-chart-wrap">
      <div className="tracking-chart-header">
        <span className="tracking-chart-label">{metric.label}</span>
        <span className="tracking-chart-unit">{metric.unit}</span>
      </div>
      <svg viewBox={`0 0 ${VW} ${VH}`} width="100%" style={{ display: 'block' }}
        onMouseLeave={() => setTooltip(null)}>
        {yLabels.map((v, i) => (
          <line key={i} x1={PL} y1={yFor(v)} x2={VW - PR} y2={yFor(v)} stroke="#f0f2f5" strokeWidth="1" />
        ))}
        <polygon points={areaPath} fill={metric.color} opacity="0.1" />
        <polyline points={pts} fill="none" stroke={metric.color} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
        {markers.map((m, i) => (
          <g key={i}>
            <line x1={m.x} y1={PT - 2} x2={m.x} y2={PT + cH}
              stroke={m.isSelected ? '#6366f1' : '#c7d2e0'}
              strokeWidth={m.isSelected ? 1.5 : 1}
              strokeDasharray={m.isSelected ? '4 2' : '2 3'}
            />
            {m.isSelected && (
              <circle cx={m.x} cy={PT - 2} r="3" fill="#6366f1" />
            )}
          </g>
        ))}
        {/* Data point dots with hover */}
        {data.map((d, i) => {
          const cx = xFor(new Date(d.date).getTime());
          const cy = yFor(d.value);
          return (
            <circle
              key={i}
              cx={cx} cy={cy} r={tooltip?.date === d.date ? 4.5 : 3}
              fill={tooltip?.date === d.date ? metric.color : '#fff'}
              stroke={metric.color} strokeWidth="1.5"
              style={{ cursor: 'crosshair', transition: 'r 0.1s' }}
              onMouseEnter={() => setTooltip({ x: cx, y: cy, date: d.date, value: d.value })}
            />
          );
        })}
        <line x1={PL} y1={PT} x2={PL} y2={PT + cH} stroke="#e5e7eb" strokeWidth="1" />
        {yLabels.map((v, i) => (
          <text key={i} x={PL - 3} y={yFor(v) + 3.5} textAnchor="end" fontSize="8" fill="#9ca3af">{v}</text>
        ))}
        {xTicks.map((t, i) => (
          <g key={i}>
            <line x1={t.x} y1={PT + cH} x2={t.x} y2={PT + cH + 3} stroke="#e5e7eb" strokeWidth="1" />
            <text x={t.x} y={VH - 2} textAnchor="middle" fontSize="7.5" fill="#9ca3af">{t.label}</text>
          </g>
        ))}
        <line x1={PL} y1={PT + cH} x2={VW - PR} y2={PT + cH} stroke="#e5e7eb" strokeWidth="1" />
        {renderTooltip()}
      </svg>
    </div>
  );
}

// ── SVG Behandlungszeitleiste ─────────────────────────────────────────────────

function TreatmentTimeline({ treatments, onSelectSession, selectedSession }) {
  const wrapRef = useRef(null);
  const [svgWidth, setSvgWidth] = useState(800);

  useEffect(() => {
    if (!wrapRef.current) return;
    const obs = new ResizeObserver(e => setSvgWidth(e[0].contentRect.width));
    obs.observe(wrapRef.current);
    setSvgWidth(wrapRef.current.clientWidth);
    return () => obs.disconnect();
  }, []);

  const treatmentNames = Object.keys(treatments);
  if (treatmentNames.length === 0) return null;

  const allDates = treatmentNames.flatMap(t => treatments[t]).map(s => new Date(s.session_date));
  const minDate = new Date(Math.min(...allDates));
  const endDate = new Date(Math.max(Math.max(...allDates), Date.now()));

  const PL = 164, PR = 60, PT = 20, ROW_H = 52, DOT_R = 7;
  const width = Math.max(svgWidth, 600);
  const trackW = width - PL - PR;
  const totalMs = endDate - minDate || 1;
  const svgH = PT + treatmentNames.length * ROW_H + 40;

  const xFor = dateStr => PL + ((new Date(dateStr) - minDate) / totalMs) * trackW;
  const nowX = PL + ((Date.now() - minDate) / totalMs) * trackW;

  const ticks = [];
  const c = new Date(minDate);
  c.setDate(1);
  c.setMonth(Math.ceil(c.getMonth() / 3) * 3);
  while (c <= endDate) { ticks.push(new Date(c)); c.setMonth(c.getMonth() + 3); }

  return (
    <div ref={wrapRef} className="timeline-svg-wrap">
      <svg width={width} height={svgH} style={{ minWidth: 600 }}>
        {ticks.map((t, i) => {
          const x = PL + ((t - minDate) / totalMs) * trackW;
          return (
            <g key={i}>
              <line x1={x} y1={PT - 6} x2={x} y2={svgH - 30} stroke="#f3f4f6" strokeWidth={1} />
              <text x={x} y={svgH - 14} textAnchor="middle" fontSize={10} fill="#9ca3af">
                {t.toLocaleDateString('de-DE', { month: 'short', year: '2-digit' })}
              </text>
            </g>
          );
        })}
        {nowX <= width - PR && (
          <g>
            <line x1={nowX} y1={PT - 6} x2={nowX} y2={svgH - 30} stroke="#6366f1" strokeWidth={1.5} strokeDasharray="4 3" />
            <text x={nowX} y={PT - 10} textAnchor="middle" fontSize={10} fill="#6366f1" fontWeight="600">Heute</text>
          </g>
        )}
        {treatmentNames.map((tname, ti) => {
          const color = TREATMENT_COLORS[ti % TREATMENT_COLORS.length];
          const sessions = treatments[tname];
          const ongoing = isOngoing(tname);
          const rowY = PT + ti * ROW_H + ROW_H / 2;
          const firstX = xFor(sessions[0].session_date);
          const lastX = xFor(sessions[sessions.length - 1].session_date);
          const lineEndX = ongoing ? Math.min(nowX, width - PR) : lastX;
          return (
            <g key={tname}>
              <text x={PL - 10} y={rowY + 4} textAnchor="end" fontSize={11} fill="#374151" fontWeight="500" style={{ userSelect: 'none' }}>
                {tname.length > 24 ? tname.slice(0, 22) + '…' : tname}
              </text>
              <line x1={firstX} y1={rowY} x2={lineEndX} y2={rowY} stroke={color} strokeWidth={3} strokeOpacity={0.35} strokeLinecap="round" />
              {ongoing && lineEndX > firstX && (
                <polygon points={`${lineEndX},${rowY} ${lineEndX - 8},${rowY - 5} ${lineEndX - 8},${rowY + 5}`} fill={color} opacity={0.5} />
              )}
              {sessions.map(s => {
                const cx = xFor(s.session_date);
                const isSel = selectedSession?.id === s.id;
                return (
                  <g key={s.id} style={{ cursor: 'pointer' }} onClick={() => onSelectSession(s)}>
                    <circle cx={cx} cy={rowY} r={DOT_R + 5} fill="transparent" />
                    <circle cx={cx} cy={rowY} r={DOT_R} fill={isSel ? color : '#fff'} stroke={color} strokeWidth={2.5} />
                    {isSel && <circle cx={cx} cy={rowY} r={DOT_R + 4} fill="none" stroke={color} strokeWidth={1.5} opacity={0.5} />}
                  </g>
                );
              })}
            </g>
          );
        })}
        <line x1={PL} y1={svgH - 28} x2={width - PR} y2={svgH - 28} stroke="#e5e7eb" strokeWidth={1} />
      </svg>
    </div>
  );
}

// ── Hauptseite ────────────────────────────────────────────────────────────────

function BehandlungsverfolgungPage() {
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [resolution, setResolution] = useState('weekly');
  const searchRef = useRef(null);

  useEffect(() => { fetchPatients().then(setPatients).catch(() => {}); }, []);

  const loadPatient = useCallback((p) => {
    setSelectedPatient(p);
    setSearch(p.patient_label);
    setShowDropdown(false);
    setSelectedSession(null);
    setLoadError('');
    setLoading(true);
    fetchArchivedSessions(p.patient_label)
      .then(data => { setSessions(data); })
      .catch(e => { setSessions([]); setLoadError(e.message || 'Fehler beim Laden'); })
      .finally(() => setLoading(false));
  }, []);

  const filtered = patients.filter(p => p.patient_label.toLowerCase().includes(search.toLowerCase()));
  const treatments = groupByTreatment(sessions);
  const treatmentNames = Object.keys(treatments);
  const activeTreatments = treatmentNames.filter(t => isOngoing(t)).length;
  const lastSession = sessions.length
    ? sessions.reduce((a, b) => new Date(a.session_date) > new Date(b.session_date) ? a : b)
    : null;

  function colorForTreatment(tname) {
    const idx = treatmentNames.indexOf(tname);
    return TREATMENT_COLORS[idx >= 0 ? idx % TREATMENT_COLORS.length : 0];
  }

  // Tracking-Daten für aktuell ausgewählte Session (lazy, nur bei Bedarf)
  const trackingMetrics = useMemo(() => {
    if (!selectedSession || !selectedPatient) return [];
    const tname = selectedSession.diagnoses?.[0]?.name || 'Sonstiges';
    const tSessions = treatments[tname] || [];
    return generateTrackingMetrics(selectedPatient.patient_label, tname, tSessions);
  }, [selectedSession, selectedPatient, treatments]);

  const patientComments = useMemo(() => {
    if (!selectedSession || !selectedPatient) return [];
    const tname = selectedSession.diagnoses?.[0]?.name || 'Sonstiges';
    const tSessions = treatments[tname] || [];
    return generatePatientComments(selectedPatient.patient_label, tname, tSessions);
  }, [selectedSession, selectedPatient, treatments]);

  const selectedSessionDateStr = selectedSession?.session_date?.slice(0, 10) || null;

  function handleSelectSession(s) {
    setSelectedSession(prev => prev?.id === s.id ? null : s);
  }

  return (
    <div className="page page-wide behandlung-page">
      {/* Header */}
      <div className="bv-header">
        <div>
          <h1 className="page-title" style={{ marginBottom: 2 }}>Behandlungsverfolgung</h1>
          <p className="page-subtitle" style={{ margin: 0 }}>Verlauf, Tracking & Patienten-Feedback</p>
        </div>
        <div className="bv-header-actions">
          <div className="patient-search-wrap">
            <svg className="patient-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              ref={searchRef}
              className="patient-search-input"
              placeholder="Patienten-ID suchen…"
              value={search}
              onChange={e => { setSearch(e.target.value); setShowDropdown(true); }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
            />
            {showDropdown && filtered.length > 0 && (
              <div className="patient-dropdown">
                {filtered.map(p => (
                  <div key={p.id} className={`patient-dropdown-item${selectedPatient?.id === p.id ? ' selected' : ''}`}
                    onMouseDown={() => loadPatient(p)}>
                    {p.patient_label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Leer-Zustand */}
      {!selectedPatient && !loading && (
        <div className="empty-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          <p>Patienten-ID eingeben oder auswählen</p>
          <small>Tipp: Demo-Patienten über den Button oben anlegen</small>
        </div>
      )}

      {loading && <div className="empty-state"><p>Lade Patientendaten…</p></div>}

      {loadError && (
        <div className="bv-error-box">
          <strong>Fehler beim Laden:</strong> {loadError}
          {(loadError.includes('401') || loadError.includes('eingeloggt')) && ' — Bitte erneut einloggen.'}
        </div>
      )}

      {selectedPatient && !loading && (
        <>
          {/* Übersichtskarte */}
          <div className="patient-overview-card">
            <div className="overview-stat">
              <div className="patient-overview-label">Patient</div>
              <div className="patient-overview-value">{selectedPatient.patient_label}</div>
              <div className="patient-overview-sub">Pseudonymisierte ID</div>
            </div>
            <div className="overview-stat">
              <div className="patient-overview-label">Sessions gesamt</div>
              <div className="patient-overview-value">{sessions.length}</div>
              <div className="patient-overview-sub">archivierte Konsultationen</div>
            </div>
            <div className="overview-stat">
              <div className="patient-overview-label">Aktive Behandlungen</div>
              <div className="patient-overview-value">{activeTreatments}</div>
              <div className="patient-overview-sub">von {treatmentNames.length} gesamt</div>
            </div>
            <div className="overview-stat">
              <div className="patient-overview-label">Letzte Vorstellung</div>
              <div className="patient-overview-value">{lastSession ? formatDate(lastSession.session_date) : '—'}</div>
              <div className="patient-overview-sub">{lastSession?.session_name || ''}</div>
            </div>
          </div>

          {sessions.length === 0 && (
            <div className="empty-state"><p>Keine archivierten Sessions für diesen Patienten</p></div>
          )}

          {sessions.length > 0 && (
            <>
              {/* Behandlungsübersicht — Kacheln */}
              <div className="bv-section">
                <h2 className="bv-section-title">Behandlungsübersicht</h2>
                <div className="treatment-tiles-row">
                  {treatmentNames.map((tname, ti) => {
                    const color = TREATMENT_COLORS[ti % TREATMENT_COLORS.length];
                    const sArr = treatments[tname];
                    const ongoing = isOngoing(tname);
                    const first = sArr[0], last = sArr[sArr.length - 1];
                    const { anamnese, befund, behandlung } = getTileContent(sArr);
                    return (
                      <div key={tname} className="treatment-tile" style={{ borderTopColor: color }}>
                        <div className="treatment-tile-top">
                          <span className="treatment-tile-name" style={{ color }}>{tname}</span>
                          <span className="treatment-tile-badge"
                            style={ongoing ? { background: '#d1fae5', color: '#065f46' } : { background: '#f3f4f6', color: '#6b7280' }}>
                            {ongoing ? 'Aktiv' : 'Abgeschlossen'}
                          </span>
                        </div>
                        <div className="treatment-tile-period">
                          {formatDate(first.session_date)} – {ongoing ? 'laufend' : formatDate(last.session_date)}
                        </div>
                        <div className="treatment-tile-summary">
                          {anamnese && (
                            <p><span className="tile-summary-label">Anamnese</span>{anamnese.length > 90 ? anamnese.slice(0, 88) + '…' : anamnese}</p>
                          )}
                          {befund && (
                            <p><span className="tile-summary-label">Befund</span>{befund.length > 90 ? befund.slice(0, 88) + '…' : befund}</p>
                          )}
                          {behandlung && (
                            <p><span className="tile-summary-label">Behandlung</span>{behandlung.length > 90 ? behandlung.slice(0, 88) + '…' : behandlung}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Zeitleiste */}
              <div className="bv-section">
                <h2 className="bv-section-title">Behandlungszeitleiste</h2>
                <TreatmentTimeline
                  treatments={treatments}
                  onSelectSession={handleSelectSession}
                  selectedSession={selectedSession}
                />
              </div>

              {/* Session-Detailansicht */}
              {selectedSession && (
                <div className="session-detail-panel" key={selectedSession.id}>
                  {/* Panel-Header */}
                  <div className="detail-panel-header">
                    <div>
                      <h3 className="detail-panel-title">Behandlung und Verlauf</h3>
                      {(() => {
                        const tname = selectedSession.diagnoses?.[0]?.name || 'Sonstiges';
                        const tSessions = treatments[tname] || [];
                        const sorted = [...tSessions].sort((a, b) => new Date(a.session_date) - new Date(b.session_date));
                        const periodStart = sorted[0] ? formatDate(sorted[0].session_date) : formatDate(selectedSession.session_date);
                        const periodEnd = isOngoing(tname) ? 'laufend' : sorted.length > 1 ? formatDate(sorted[sorted.length - 1].session_date) : periodStart;
                        return (
                          <div className="detail-panel-date">
                            {periodStart} – {periodEnd}
                            <span style={{ margin: '0 6px', opacity: 0.4 }}>·</span>
                            {selectedSession.session_name}
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Medizinische Zusammenfassung — 3 Spalten: Anamnese | Diagnose & Befund | Therapie */}
                  {(() => {
                    const { anamnese: anamSecs, befund: befundSecs, diagnose: diagList, therapie: therSecs } = getDetailSections(selectedSession);
                    const colColor = colorForTreatment(selectedSession.diagnoses?.[0]?.name || '');
                    return (
                      <div className="detail-three-cols">
                        {/* Anamnese */}
                        <div className="detail-col-card" style={{ borderTopColor: colColor }}>
                          <h4 className="detail-col-title">Anamnese</h4>
                          {anamSecs.length > 0 ? anamSecs.map((sec, i) => (
                            <div key={i} className="detail-col-section">
                              {sec.title && <div className="detail-col-section-label">{sec.title}</div>}
                              <ul>{(sec.items || []).map((item, j) => <li key={j}>{item}</li>)}</ul>
                            </div>
                          )) : <p className="detail-col-empty">Keine Angaben</p>}
                        </div>

                        {/* Diagnose & Befund */}
                        <div className="detail-col-card" style={{ borderTopColor: colColor }}>
                          <h4 className="detail-col-title">Diagnose & Befund</h4>
                          {diagList.length > 0 && diagList.map((d, i) => (
                            <div key={i} className="detail-col-diagnosis">
                              <div className="detail-col-diag-name">{d.name}</div>
                              {d.reason && <p className="detail-col-diag-reason">{d.reason}</p>}
                            </div>
                          ))}
                          {befundSecs.length > 0 && befundSecs.map((sec, i) => (
                            <div key={`b${i}`} className="detail-col-section">
                              {sec.title && <div className="detail-col-section-label">{sec.title}</div>}
                              <ul>{(sec.items || []).map((item, j) => <li key={j}>{item}</li>)}</ul>
                            </div>
                          ))}
                          {diagList.length === 0 && befundSecs.length === 0 && (
                            <p className="detail-col-empty">Keine Diagnosen hinterlegt</p>
                          )}
                        </div>

                        {/* Therapie & Medikation */}
                        <div className="detail-col-card" style={{ borderTopColor: colColor }}>
                          <h4 className="detail-col-title">Therapie & Medikation</h4>
                          {therSecs.length > 0 ? therSecs.map((sec, i) => (
                            <div key={i} className="detail-col-section">
                              {sec.title && <div className="detail-col-section-label">{sec.title}</div>}
                              <ul>{(sec.items || []).map((item, j) => <li key={j}>{item}</li>)}</ul>
                            </div>
                          )) : <p className="detail-col-empty">Keine Angaben</p>}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Interne Notizen */}
                  {selectedSession.private_notes && (
                    <div className="detail-private-notes">
                      <span className="detail-private-title">Interne Notizen</span>
                      <span className="detail-private-badge">Nur für Sie</span>
                      <p>{selectedSession.private_notes}</p>
                    </div>
                  )}

                  {/* Befunddokumente */}
                  {(() => {
                    const docs = selectedSession.uploaded_docs || [];
                    if (!docs.length) return null;
                    const catLabel = { pdf: 'PDF', docx: 'Word', image: 'Bild', text: 'Text', other: 'Datei' };
                    return (
                      <div style={{ margin: '16px 0', padding: '14px 16px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 8 }}>Befunddokumente</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                          {docs.map((doc, i) => (
                            <div key={doc.id || i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 7 }}>
                              <span style={{ fontSize: 10, fontWeight: 700, background: '#eef2ff', color: '#4f46e5', padding: '1px 7px', borderRadius: 10, flexShrink: 0 }}>
                                {catLabel[doc.category] || 'Datei'}
                              </span>
                              <span style={{ fontSize: 13, color: '#374151', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {doc.name}
                              </span>
                              <span style={{ fontSize: 11, color: '#9ca3af', flexShrink: 0 }}>
                                {doc.size < 1024 ? `${doc.size} B` : doc.size < 1048576 ? `${Math.round(doc.size / 1024)} KB` : `${(doc.size / 1048576).toFixed(1)} MB`}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Patienten-Feedback / Tracking */}
                  <div className="detail-tracking-section">
                    <div className="detail-tracking-header">
                      <div>
                        <span className="detail-tracking-title">Patienten-Feedback</span>
                        <span className="detail-tracking-sub">Einträge aus der Patienten-App (simuliert) · Sessions als Marker · Auflösung wählen</span>
                      </div>
                      <div className="resolution-tabs">
                        {[['daily', 'Täglich'], ['weekly', 'Wöchentlich'], ['monthly', 'Monatlich']].map(([val, label]) => (
                          <button
                            key={val}
                            className={`resolution-tab${resolution === val ? ' active' : ''}`}
                            onClick={() => setResolution(val)}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {trackingMetrics.length === 0 ? (
                      <p style={{ fontSize: 13, color: '#9ca3af', padding: '12px 0' }}>Keine Verlaufsdaten verfügbar.</p>
                    ) : (
                      <div className="tracking-charts-stack">
                        {trackingMetrics.map(m => (
                          <TrackingChart
                            key={m.id}
                            metric={m}
                            resolution={resolution}
                            selectedSessionDateStr={selectedSessionDateStr}
                          />
                        ))}
                      </div>
                    )}

                    {/* Patienten-Kommentare */}
                    {patientComments.length > 0 && (
                      <div className="patient-comments-section">
                        <div className="patient-comments-header">
                          <span className="patient-comments-title">Patienten-Nachrichten</span>
                          <span className="patient-comments-sub">Einträge aus der Patienten-App (simuliert) · Nebenwirkungen, Rückfragen, Verlauf</span>
                        </div>
                        <div className="patient-comments-list">
                          {patientComments.map((c, i) => {
                            const meta = COMMENT_TYPE_META[c.type] || COMMENT_TYPE_META.concern;
                            return (
                              <div key={i} className="patient-comment"
                                style={{ background: meta.bg, borderLeft: `3px solid ${meta.color}` }}>
                                <div className="patient-comment-meta">
                                  <span className="patient-comment-date">{formatDate(c.date)}</span>
                                  <span className="patient-comment-badge" style={{ color: meta.color, background: 'rgba(255,255,255,0.7)', border: `1px solid ${meta.border}` }}>
                                    {meta.label}
                                  </span>
                                </div>
                                <p className="patient-comment-text">{c.text}</p>
                              </div>
                            );
                          })}
                        </div>
                        <p className="patient-comments-future">
                          In einer späteren Version können Arzt und Patient hier direkt kommunizieren und eigene Marker setzen.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default BehandlungsverfolgungPage;
